import React, { useState, useEffect, useMemo, useCallback, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';

import { Div, Row, Col, Icon, Text, Switch } from '@pinpoint/design-kit';
import { charts, styles } from '@pinpoint/onpoint-charts';

import DefaultTheme from 'src/styles/DefaultTheme';
import { formatNumberV2 } from 'src/functions';
import { Button } from 'src/components';
import CONSTANTS from 'src/store/constants';

const chartActiveColor = (testVal, chartColor) => (testVal ? chartColor : 'shade99');
const greyActiveColor = (testVal) => (testVal ? 'shade99' : 'shade66');

const formatQuarter = (val) => {
  const date = new Date(val);
  const year = date.getUTCFullYear() % 100;
  const month = date.getUTCMonth();
  const quarter = Math.floor(month / 3) + 1;
  return `${year.toString().padStart(2, '0')}Q${quarter}`;
};

const INPUTS = {
  [CONSTANTS.INPUT_DISTRIBUTION.INPUTS.POLICY_PERIODS]: {
    COLOR: 'actuarialDir',
    COLOR_HEX: DefaultTheme.colors.actuarialDir,
    HEADER_NAME: 'Policy Periods',
    DATASET_KEY: 'onb_sum_policy_periods',
    format: (val) =>
      new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(val < 0 ? 0 : val),
    formatXAxis: (val) =>
      new Intl.NumberFormat('en-US', {
        minimumFractionDigits: val > 50 ? 1 : 2,
        maximumFractionDigits: val > 50 ? 1 : 2,
      }).format(val < 0 ? 0 : val),
  },
  [CONSTANTS.INPUT_DISTRIBUTION.INPUTS.OTHER_EXPOSURE]: {
    COLOR: 'otherExposure',
    COLOR_HEX: DefaultTheme.colors.otherExposure,
    HEADER_NAME: 'Other Exposure',
    DATASET_KEY: 'onb_other_exposure',
    format: (val) =>
      new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(val < 0 ? 0 : val),
    formatXAxis: (val) =>
      new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(val < 0 ? 0 : val),
  },
  [CONSTANTS.INPUT_DISTRIBUTION.INPUTS.LOSS_COUNT]: {
    COLOR: 'actuarialFreq',
    COLOR_HEX: DefaultTheme.colors.actuarialFreq,
    HEADER_NAME: 'Claims Count',
    DATASET_KEY: 'onb_sum_loss_counts',
    format: (val) => Math.floor(val),
    formatTooltip: (data) => {
      let value;
      if (data[0] <= 0) {
        // 0 bucket
        value = 0;
      } else if (data[0] - data[2] <= 1) {
        // display single bucket value when range isn't greater than 1
        value = formatNumberV2(Math.floor(data[0]));
      } else {
        value = `${formatNumberV2(Math.ceil(data[2]))} - ${formatNumberV2(Math.floor(data[0]))}`;
      }
      return `Claims Count: ${value}`;
    },
  },
  [CONSTANTS.INPUT_DISTRIBUTION.INPUTS.LOSS_DOLLARS]: {
    COLOR: 'actuarialSev',
    COLOR_HEX: DefaultTheme.colors.actuarialSev,
    HEADER_NAME: 'Loss Dollars and Expenses',
    DATASET_KEY: 'onb_sum_loss_dollars_and_expenses',
    format: (val) =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(val < 0 ? 0 : val),
    formatXAxis: (val) =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(val < 0 ? 0 : val),
  },
  [CONSTANTS.INPUT_DISTRIBUTION.INPUTS.PREMIUMS]: {
    COLOR: 'actuarialMult',
    COLOR_HEX: DefaultTheme.colors.actuarialMult,
    HEADER_NAME: 'Premiums Earned',
    DATASET_KEY: 'onb_sum_premiums_earned',
    format: (val) =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(val < 0 ? 0 : val),
    formatXAxis: (val) =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(val < 0 ? 0 : val),
  },
  [CONSTANTS.INPUT_DISTRIBUTION.INPUTS.EFFECTIVE_DATE]: {
    COLOR: 'brand300',
    COLOR_HEX: DefaultTheme.colors.brand300,
    HEADER_NAME: 'Effective Quarters',
    DATASET_KEY: 'onb_sum_effective_date',
    SWITCH_BUCKET_DISPLAY: true,
    format: formatQuarter,
    formatXAxis: formatQuarter,
    formatTooltip: (data) => `Calendar Quarter: ${formatQuarter(data[0])}`,
  },
};

const FILTERS = {
  [CONSTANTS.INPUT_DISTRIBUTION.FILTERS.CLAIMANTS]: ({ claimants }) => claimants,
  [CONSTANTS.INPUT_DISTRIBUTION.FILTERS.NON_CLAIMANTS]: ({ non_claimants: nonClaimants }) => nonClaimants,
  [CONSTANTS.INPUT_DISTRIBUTION.FILTERS.ALL]: ({ count }) => count,
};

/**
 * InputDistribution - Input distribution charts for onboarding
 *
 * @param {Object} props - components props
 * @param {Object} props.history - history store object
 * @param {string|null|undefined} props.email - profile email
 * @param {string|null|undefined} props.queryParams - unprocessed query params
 * @param {string|null|undefined} props.pathname - current page path (already has interpolated values)
 * @param {Object|null|undefined} props.onpointDataset - onpoint dataset
 */
function InputDistributions({ email, history, queryParams, pathname, onpointDataset }) {
  const inputData = onpointDataset?.payload?.onb_column_counts;

  const [inputType, setInputType] = useState(CONSTANTS.INPUT_DISTRIBUTION.INPUTS.POLICY_PERIODS);
  const [filter, setFilter] = useState(CONSTANTS.INPUT_DISTRIBUTION.FILTERS.ALL);
  const [yAxisLog, setYAxisLog] = useState(false);
  const [xAxisLog, setXAxisLog] = useState(false);

  const handleFilter = useCallback((bin) => FILTERS[filter](bin), [filter]);

  const inputConfig = useMemo(() => INPUTS[inputType], [inputType]);
  const chartDataset = useMemo(() => {
    const processed =
      inputData[inputConfig.DATASET_KEY]?.map((bin) => {
        const { left, right } = bin;
        const count = handleFilter(bin);
        return inputConfig.SWITCH_BUCKET_DISPLAY
          ? [left, count || +yAxisLog, right]
          : [right, count || +yAxisLog, left]; // NOTE: count || +yAxisLog, prevents 0 if in log mode
      }) ?? [];
    return processed.slice(+xAxisLog); // NOTE: coerce bool to 1 or 0, slice 0 is equal to the full array
  }, [inputData, inputConfig, handleFilter, yAxisLog, xAxisLog]);

  // When the chart changes update the query param
  useEffect(() => {
    const query = new URLSearchParams(queryParams);
    const urlInputType = query.get('variant');
    if (!urlInputType) {
      query.append('variant', 'policy-periods');
      history.replace(`${pathname}?${query.toString()}`);
    } else {
      setInputType(CONSTANTS.INPUT_DISTRIBUTION.PARAMS[urlInputType]);
    }
  }, [queryParams, history, pathname, inputType]);

  useLayoutEffect(() => {
    ReactTooltip.rebuild();
  });

  return (
    <Div
      // h="100%"
      w="100%"
      bg="white"
      overflow="hidden"
      rounded="md"
      data-debug="container"
      flexDir="column"
      d="flex"
      flexWrap="nowrap"
      flexGrow="1"
    >
      <ReactTooltip id="input-distributions-tooltip" className="pinpoint-tooltip" effect="solid" delayShow={500} />
      <Row m={{ x: '1.5rem', t: '1.5rem', b: '.9rem' }} data-debug="chart-views-row">
        <Col gutterWidth="0" d="inline-flex" size="6" data-test="view-buttons">
          <Button
            buttonStyle="tab"
            selected={inputType === CONSTANTS.INPUT_DISTRIBUTION.INPUTS.POLICY_PERIODS}
            onClick={() => history.push(`${pathname}?variant=policy-periods`)}
            data-tip="View distribution of policy periods"
            data-for="input-distributions-tooltip"
          >
            <Icon name="CalendarEmpty" viewBox="0 0 19 15" color="white" m={{ r: '.5em' }} />
            POLICY PERIODS
          </Button>
          {INPUTS[CONSTANTS.INPUT_DISTRIBUTION.INPUTS.OTHER_EXPOSURE].DATASET_KEY in inputData && (
            <Button
              buttonStyle="tab"
              selected={inputType === CONSTANTS.INPUT_DISTRIBUTION.INPUTS.OTHER_EXPOSURE}
              onClick={() => history.push(`${pathname}?variant=other-exposure`)}
              data-tip="View distribution of other exposure"
              data-for="input-distributions-tooltip"
            >
              <Icon name="DollarsClock" viewBox="0 0 19 17" color="white" m={{ r: '.5em' }} />
              OTHER EXPOSURE
            </Button>
          )}
          <Button
            buttonStyle="tab"
            selected={inputType === CONSTANTS.INPUT_DISTRIBUTION.INPUTS.LOSS_COUNT}
            onClick={() => history.push(`${pathname}?variant=claims-count`)}
            data-tip="View distribution of claims count"
            data-for="input-distributions-tooltip"
          >
            <Icon name="CounterTower" viewBox="0 0 19 16" color="white" m={{ r: '.5rem' }} />
            CLAIMS COUNT
          </Button>
          <Button
            buttonStyle="tab"
            selected={inputType === CONSTANTS.INPUT_DISTRIBUTION.INPUTS.LOSS_DOLLARS}
            onClick={() => history.push(`${pathname}?variant=loss-dollars`)}
            data-tip="View distribution of loss dollars and expenses"
            data-for="input-distributions-tooltip"
          >
            <Icon name="DollarsLoss" viewBox="0 0 20 16" color="white" m={{ r: '.5rem' }} />
            LOSS DOLLARS
          </Button>
          <Button
            buttonStyle="tab"
            selected={inputType === CONSTANTS.INPUT_DISTRIBUTION.INPUTS.PREMIUMS}
            onClick={() => history.push(`${pathname}?variant=premiums`)}
            data-tip="View distribution of premiums earned"
            data-for="input-distributions-tooltip"
          >
            <Icon name="DollarsPremium" viewBox="0 0 18 18" color="white" m={{ r: '.5rem' }} />
            PREMIUMS
          </Button>
          {INPUTS[CONSTANTS.INPUT_DISTRIBUTION.INPUTS.EFFECTIVE_DATE].DATASET_KEY in inputData && (
            <Button
              buttonStyle="tab"
              selected={inputType === CONSTANTS.INPUT_DISTRIBUTION.INPUTS.EFFECTIVE_DATE}
              onClick={() => history.push(`${pathname}?variant=effective-date`)}
              data-tip="View distribution of effective dates"
              data-for="input-distributions-tooltip"
            >
              <Icon name="CalendarEmpty" viewBox="0 0 19 15" color="white" m={{ r: '.5em' }} />
              EFFECTIVE DATES
            </Button>
          )}
        </Col>
        <Col flexGrow="1" d="flex" justify="right" align="center" gutterWidth="0" data-test="filter-buttons">
          <Icon
            name="Filter"
            viewBox="0 0 17 16"
            size="1rem"
            m={{ x: '.625rem' }}
            data-tip="View filter"
            data-for="input-distributions-tooltip"
          />
          <Button
            buttonStyle="tab"
            selected={filter === CONSTANTS.INPUT_DISTRIBUTION.FILTERS.CLAIMANTS}
            onClick={() => setFilter(CONSTANTS.INPUT_DISTRIBUTION.FILTERS.CLAIMANTS)}
            data-tip="View claimants only"
            data-for="input-distributions-tooltip"
          >
            <Icon name="DollarsUmbrella" viewBox="0 0 17 19" color="white" m={{ r: '.5rem' }} />
            CLAIMANTS
          </Button>
          <Button
            buttonStyle="tab"
            selected={filter === CONSTANTS.INPUT_DISTRIBUTION.FILTERS.NON_CLAIMANTS}
            onClick={() => setFilter(CONSTANTS.INPUT_DISTRIBUTION.FILTERS.NON_CLAIMANTS)}
            data-tip="View non-claimants only"
            data-for="input-distributions-tooltip"
          >
            <Icon name="PersonUmbrella" viewBox="0 0 16 19" color="white" m={{ r: '.5rem' }} />
            NON-CLAIMANTS
          </Button>
          <Button
            buttonStyle="tab"
            m={{ r: '0rem' }}
            selected={filter === CONSTANTS.INPUT_DISTRIBUTION.FILTERS.ALL}
            onClick={() => setFilter(CONSTANTS.INPUT_DISTRIBUTION.FILTERS.ALL)}
            data-tip="View all insureds"
            data-for="input-distributions-tooltip"
          >
            ALL
          </Button>
        </Col>
      </Row>
      <Row bg="white" flexGrow="1" rounded="md" p={{ x: '1.5rem', y: '1.25rem' }} m={{ t: '1rem', x: '1rem' }}>
        <Col gutterWidth="0" size={12}>
          <charts.Base.Histogram
            showPalette={false}
            showPaletteControl={false}
            options={{
              grid: {
                ...styles.Style1.global.grid,
                ...styles.Style1.Histogram.grid,
                top: '10%',
                bottom: '10%',
              },
              textStyle: {
                ...styles.Style1.Histogram.textStyle,
              },
              label: {
                ...styles.Style1.Histogram.label,
              },
              legend: {
                show: false,
              },
              backgroundColor: styles.Style1.global.backgroundColor,
              tooltip: {
                show: true,
                backgroundColor: '#555',
                textColor: '#fff',
                borderWidth: 0,
                formatter: ({ data }) =>
                  data &&
                  `
                          <div style='padding:.75rem 1rem;line-height: 1.4;font-size:1rem;color: #fff;'>
                            ${formatNumberV2(data[1])} insureds
                            <br />
                            ${
                              inputConfig?.formatTooltip?.(data) ||
                              `${inputConfig.HEADER_NAME} between: ${inputConfig.format(
                                data[2]
                              )} - ${inputConfig.format(data[0])}`
                            }
                          </div>
                        `,
              },
              xAxis: {
                ...styles.Style1.Histogram.xAxis,
                name: inputConfig.HEADER_NAME,
                nameGap: 35,
                type: xAxisLog ? 'log' : 'category',
                axisLabel: {
                  ...styles.Style1.Histogram.xAxis.axisLabel,
                  formatter: inputConfig.formatXAxis || inputConfig.format,
                },
                nameTextStyle: {
                  ...styles.Style1.Histogram.xAxis.nameTextStyle,
                },
              },
              yAxis: {
                ...styles.Style1.Histogram.yAxis,
                type: yAxisLog ? 'log' : 'value',
                name: 'Number of insureds',
              },
              dataset: {
                source: chartDataset,
                sourceHeader: false,
              },
              series: [
                {
                  ...styles.Style1.PxHistogram.series,
                  name: 'Score',
                  type: 'bar',
                  color: inputConfig.COLOR_HEX,
                  itemStyle: {
                    barBorderRadius: [10, 10, 0, 0],
                    opacity: 0.75,
                    color: inputConfig.COLOR_HEX,
                  },
                  encode: {
                    x: 0,
                    y: 1,
                  },
                  barMaxWidth: xAxisLog ? '8' : null,
                  barCategoryGap: xAxisLog ? '80%' : '15%',
                  animationEasing: 'quinticInOut',
                },
              ],
            }}
          />
        </Col>
      </Row>
      <Row data-debug="spacer2" m={{ x: '0rem' }} p={{ x: '2rem' }}>
        <Col
          size={12}
          gutterWidth="0"
          data-debug="left-toggles-col"
          borderColor="shadeE6"
          border={{ t: '1px solid' }}
        />
      </Row>
      <Row data-debug="toggle-row" p={{ t: '1rem', b: '1.5rem' }} data-test="toggle-row">
        <Col
          size={4}
          data-debug="left-toggle-col"
          gutterWidth="6rem"
          d="flex"
          justify="left"
          align="center"
          flexWrap="nowrap"
        >
          <Text
            textSize="subtitle"
            h="1.125rem"
            textColor="shade66"
            style={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}
            data-tip="Convert Y-axis to log scale"
            data-for="input-distributions-tooltip"
          >
            Y-axis log:
          </Text>
          <Text
            h="1.125rem"
            m={{ l: '0.5rem' }}
            textSize="subtitle"
            textColor={greyActiveColor(yAxisLog)}
            style={{ whiteSpace: 'nowrap' }}
          >
            Off
          </Text>
          <Div disabled alignSelf="center" m={{ x: '1rem' }} onClick={() => setYAxisLog(!yAxisLog)}>
            <Switch
              m="0"
              checked={yAxisLog}
              activeColor={chartActiveColor(yAxisLog, inputConfig.COLOR)}
              inactiveColor={chartActiveColor(yAxisLog, inputConfig.COLOR)}
              activeShadow="0"
              inactiveShadow="0"
            />
          </Div>
          <Text
            h="1.125rem"
            textSize="subtitle"
            textColor={chartActiveColor(yAxisLog, inputConfig.COLOR)}
            style={{ whiteSpace: 'nowrap' }}
          >
            On
          </Text>
        </Col>
        <Col
          size={4}
          data-debug="center-toggles-col"
          gutterWidth="0"
          d="flex"
          justify="center"
          align="center"
          flexWrap="nowrap"
        >
          <Text
            textSize="subtitle"
            h="1.125rem"
            textColor="shade66"
            style={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}
            data-tip="Convert X-axis to log scale"
            data-for="input-distributions-tooltip"
          >
            X-axis log:
          </Text>
          <Text
            h="1.125rem"
            m={{ l: '0.5rem' }}
            textSize="subtitle"
            textColor={greyActiveColor(xAxisLog)}
            style={{ whiteSpace: 'nowrap' }}
          >
            Off
          </Text>
          <Div alignSelf="center" m={{ x: '1rem' }} onClick={() => setXAxisLog(!xAxisLog)}>
            <Switch
              m="0"
              checked={xAxisLog}
              activeColor={chartActiveColor(xAxisLog, inputConfig.COLOR)}
              inactiveColor={chartActiveColor(xAxisLog, inputConfig.COLOR)}
              activeShadow="0"
              inactiveShadow="0"
            />
          </Div>
          <Text
            textSize="subtitle"
            h="1.125rem"
            textColor={chartActiveColor(xAxisLog, inputConfig.COLOR)}
            style={{ whiteSpace: 'nowrap' }}
          >
            On
          </Text>
        </Col>
      </Row>
    </Div>
  );
}

InputDistributions.propTypes = {
  history: PropTypes.shape({ push: PropTypes.func.isRequired, replace: PropTypes.func.isRequired }).isRequired,
  queryParams: PropTypes.string.isRequired,
  pathname: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  onpointDataset: PropTypes.oneOf([
    null,
    PropTypes.shape({
      paylaod: PropTypes.shape({
        onb_column_counts: PropTypes.oneOf([undefined, PropTypes.shape({})]).isRequired,
      }).isRequired,
    }).isRequired,
  ]).isRequired,
};

export default React.memo(InputDistributions);
